<script>
import { mapState } from 'vuex';
import findIndex from 'lodash/findIndex';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import QuotePositionModel from '@models/QuotePosition';
import QuoteModel from '@models/Quote';
import QuotePositionForm from '@views/quotes/positions/position-form';
import QuotePositionModal from '@views/quotes/positions/edit-position';
import utility from '@mixins/utility';
import datePickerUtility from '@mixins/datePickerUtility';
import draggable from 'vuedraggable';

export default {
  components: {
    FontAwesomeIcon,
    QuotePositionForm,
    QuotePositionModal,
    draggable,
  },
  mixins: [utility, datePickerUtility],
  props: {
    collapsed: {
      default: false,
      type: Boolean,
      required: false,
    },
    // eslint-disable-next-line
    quote: {
      required: true,
      default: null,
    },
    quotePositions: {
      default: () => [],
      type: Array,
    },
  },
  data() {
    return {
      selectedQuotePosition: null,
      quotePositionsLocal: this.quotePositions,
      duplicateQuotePosition: null,
      showDuplicateQuotePositionModal: false,
      num_of_duplicates: 1,
      fields: [
        {
          key: 'drag_handle',
          label: '',
          sortable: false,
          thStyle: { width: '40px' },
          tdClass: 'text-center',
        },
        {
          key: 'order',
          label: this.$t('fields.position'),
          sortable: false,
        },
        {
          key: 'formatted_date',
          label: this.$t('fields.date'),
          sortable: false,
        },
        {
          key: 'description',
          label: this.$t('fields.description'),
          sortable: false,
        },
        {
          key: 'amount',
          label: this.$t('table_labels.amount'),
          sortable: false,
        },
        {
          key: 'formatted_price',
          label: this.$t('table_labels.unit_price'),
          sortable: false,
        },
        {
          key: 'formatted_total',
          label: this.$t('table_labels.sum_price'),
          sortable: false,
        },
        {
          key: 'actions',
          label: this.$t('table_labels.actions'),
          sortable: false,
        },
      ],
      quotePosition: {
        date: null,
        description: null,
        remark: null,
        price: null,
        amount: null,
        notes: null,
        cancellation_policy: null,
        payment_terms: null,
      },
      defaultQuotePosition: {
        date: null,
        description: null,
        remark: null,
        price: null,
        amount: null,
        notes: null,
        cancellation_policy: null,
        payment_terms: null,
      },
      sortBy: null,
      sortDesc: false,
    };
  },
  computed: {
    ...mapState({
      dateConfig: (state) => state.common.dateConfigTime,
    }),
    subtotal() {
      return this.quotePositionsLocal.reduce((sum, position) => {
        const total = parseFloat(position.total) || 0;
        return sum + total;
      }, 0);
    },
    sortedQuotePositions() {
      if (!this.sortBy) {
        return this.quotePositionsLocal;
      }
      
      const sorted = [...this.quotePositionsLocal].sort((a, b) => {
        if (this.sortBy === 'formatted_date') {
          // Parse dates for proper comparison
          const dateA = a.date ? new Date(a.date) : new Date(0);
          const dateB = b.date ? new Date(b.date) : new Date(0);
          return this.sortDesc ? dateB - dateA : dateA - dateB;
        }
        return 0;
      });
      
      return sorted;
    },
  },
  methods: {
    toggleSort(field) {
      if (this.sortBy === field) {
        if (!this.sortDesc) {
          // First click was asc, now go to desc
          this.sortDesc = true;
        } else {
          // Second click was desc, now remove sorting
          this.sortBy = null;
          this.sortDesc = false;
        }
      } else {
        // New field, start with asc
        this.sortBy = field;
        this.sortDesc = false;
      }
    },
    onDragEnd() {
      this.drag = false;
      this.updatePositionsOrder();
    },

    async updatePositionsOrder() {
      const orderData = this.quotePositionsLocal.map((position, index) => ({
        id: position.id,
        order: index,
      }));

      this.$wait.start('updating-positions-order');
      try {
        await this.$http.post(`admin/quotes/${this.quote.id}/positions/reorder`, {
          positions: orderData,
        }).then((response) => {
          this.quotePositionsLocal = response.data.positions;
        });
      } catch (error) {
        console.error('Failed to update order:', error);
      } finally {
        this.$wait.end('updating-positions-order');
      }
    },
    submit(quotePosition) {
      this.$emit('submitted', quotePosition);
    },
    update(quotePosition) {
      this.selectedQuotePosition = null;
      const key = findIndex(this.quotePositionsLocal, { id: quotePosition.id });
      this.$emit('updated', quotePosition, key);
    },
    deleteFromModal(quotePositionId) {
      this.selectedQuotePosition = null;
      const key = findIndex(this.quotePositionsLocal, { id: quotePositionId });
      if (key !== -1) {
        this.$emit('deleted', quotePositionId, key);
      }
    },
    async deleteQuotePosition(item) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.quote_position') }))) {
        this.$wait.start(`quote_position${item.id}`);
        const key = findIndex(this.quotePositionsLocal, { id: item.id });
        const quote = new QuoteModel({ id: this.quote.id });
        const quotePosition = new QuotePositionModel({ id: item.id });
        await quotePosition.for(quote).delete();
        this.$emit('deleted', item.id, key);
        this.$wait.end(`quote_position${item.id}`);
      }
    },
    timeFormatter(value) {
      return value ? DateTime.fromSQL(value).toFormat('dd.LL.yyyy HH:mm') : null;
    },  
    formatPrice(value) {
      if (value === null || value === undefined || value === '') {
        return '0,00';
      }
      const num = parseFloat(value);
      if (isNaN(num)) {
        return '0,00';
      }

      // Format with 2 decimals
      const formatted = num.toFixed(2);

      // Split into integer and decimal parts
      const parts = formatted.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];

      // Add thousand separators (dots) to integer part
      const withThousandSeparators = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

      // Combine with comma as decimal separator
      return `${withThousandSeparators},${decimalPart}`;
    },
    async duplicate() {
      if (this.duplicateQuotePosition && this.num_of_duplicates) {
        this.$wait.start('quote position processing');
        this.$http
          .post(`admin/quote-positions/${this.duplicateQuotePosition}/duplicate`, {
            num_of_duplicates: this.num_of_duplicates,
          })
          .then((response) => {
            this.$wait.end('quote position processing');
            this.$emit('duplicated', response.data.quote_positions);
            this.closeDuplicateQuotePositionModal();
          })
          .catch(() => {
            this.$wait.end('quote position processing');
            this.closeDuplicateQuotePositionModal();
          });
      } else {
        this.$toastead.error('Quote position not selected or number of duplicates is < 1');
      }
    },
    openDuplicateQuotePositionModal(id) {
      this.duplicateQuotePosition = id;
      this.showDuplicateQuotePositionModal = true;
    },
    closeDuplicateQuotePositionModal() {
      this.duplicateQuotePosition = null;
      this.showDuplicateQuotePositionModal = false;
      this.num_of_duplicates = 1;
    },
  },
};
</script>

<template>
  <div>
    <b-collapse id="quote-positions-collapse" v-model="collapsed" class="mt-2 mb-2">
      <QuotePositionForm ref="quotePositionForm" :quote="quote" @submitted="submit" />
    </b-collapse>
    <!-- Desktop Table View -->
    <div v-if="quotePositionsLocal.length" class="d-none d-lg-block">
      <div class="table-responsive" style="overflow: inherit">
        <table class="table table-sm table-striped table-hover no-min-width b-table">
          <thead>
            <tr>
              <th style="width: 40px;"></th>
              <th>{{ $t('table_labels.position') }}</th>
              <th style="cursor: pointer; user-select: none;" @click="toggleSort('formatted_date')">
                {{ $t('fields.date') }}
                <FontAwesomeIcon v-if="sortBy === 'formatted_date'" :icon="['fas', sortDesc ? 'arrow-down' : 'arrow-up']" class="ml-1" />
                <FontAwesomeIcon v-else :icon="['fas', 'sort']" class="ml-1 text-muted" />
              </th>
              <th>{{ $t('fields.description') }}</th>
              <th>{{ $t('table_labels.amount') }}</th>
              <th>{{ $t('table_labels.unit_price') }}</th>
              <th>{{ $t('table_labels.sum_price') }}</th>
              <th>{{ $t('table_labels.actions') }}</th>
            </tr>
          </thead>
          <draggable
            v-model="quotePositionsLocal"
            handle=".drag-handle"
            ghost-class="sortable-ghost"
            drag-class="sortable-drag"
            tag="tbody"
            @end="onDragEnd"
          >
            <tr v-for="(row, index) in sortedQuotePositions" :key="`position-${row.id}-${index}`">
              <td class="text-center">
                <div class="drag-handle" style="cursor: move; user-select: none;">
                  <FontAwesomeIcon :icon="['fas', 'grip-vertical']" />
                </div>
              </td>
              <td>{{ row.order + 1 }}</td>
              <td>{{ row.formatted_date }}</td>
              <td v-if="row.description_id">
                <span class="d-block">{{ quote.language == 'en' ? row.description_field.description_en : row.description_field.description_de }}</span>
                <span v-if="row.remark" class="d-block text-muted small">{{ row.remark }}</span>
              </td>
              <td v-else>
                <span class="d-block">{{ row.description }}</span>
                <span v-if="row.remark" class="d-block text-muted small">{{ row.remark }}</span>
              </td>
              <td>{{ row.amount }}</td>
              <td>{{ row.formatted_price }}</td>
              <td>{{ row.formatted_total }}</td>
              <td>
                <b-dropdown variant="outline-primary" dropleft no-caret size="sm">
                  <template #button-content>
                    <FontAwesomeIcon :icon="['fas', 'ellipsis-h']" />
                  </template>

                  <b-dropdown-item variant="primary" :disabled="$wait.is(`quotePosition${row.id}`)" @click="selectedQuotePosition = row.id">
                    <FontAwesomeIcon :icon="['fas', 'edit']" />
                    <small>&nbsp;{{ $t('actions.edit') }}</small>
                  </b-dropdown-item>

                  <b-dropdown-item variant="info" :disabled="$wait.is(`quotePosition${row.id}`)" @click="openDuplicateQuotePositionModal(row.id)">
                    <FontAwesomeIcon :icon="['fas', 'copy']" />
                    <small>&nbsp;{{ $t('actions.duplicate') }}</small>
                  </b-dropdown-item>

                  <b-dropdown-item variant="danger" :disabled="$wait.is(`quotePosition${row.id}`)" @click="deleteQuotePosition(row)">
                    <FontAwesomeIcon :icon="['fas', 'times-circle']" />
                    <small>&nbsp;{{ $t('actions.delete') }}</small>
                  </b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
          </draggable>
          <tfoot>
            <tr class="summary-row">
              <td colspan="6" class="text-right font-weight-bold border-top">
                {{ $t('table_labels.subtotal') }}:
              </td>
              <td class="font-weight-bold border-top">
                {{ formatPrice(subtotal) }}
              </td>
              <td class="border-top"></td>
            </tr>
            <tr v-if="quote && quote.total !== null && quote.total !== undefined" class="summary-row total-row">
              <td colspan="6" class="text-right font-weight-bold border-top-thick">
                <span v-if="quote.add_tax">{{ $t('table_labels.total') }} {{ $t('table_labels.incl_tax') }}:</span>
                <span v-else>{{ $t('table_labels.total') }}:</span>
              </td>
              <td class="font-weight-bold border-top-thick">
                {{ quote.total }}
              </td>
              <td class="border-top-thick"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Mobile Card View - Full Card Draggable -->
    <div v-if="quotePositionsLocal.length" class="d-lg-none">
      <div class="mb-2">
        <b-button size="sm" variant="outline-secondary" @click="toggleSort('formatted_date')">
          <FontAwesomeIcon :icon="['fas', 'calendar']" />
          {{ $t('fields.date') }}
          <FontAwesomeIcon v-if="sortBy === 'formatted_date'" :icon="['fas', sortDesc ? 'arrow-down' : 'arrow-up']" class="ml-1" />
          <FontAwesomeIcon v-else :icon="['fas', 'sort']" class="ml-1" />
        </b-button>
      </div>
      <draggable
        v-model="quotePositionsLocal"
        handle=".mobile-drag-handle"
        ghost-class="sortable-ghost-mobile"
        drag-class="sortable-drag-mobile"
        @end="onDragEnd"
      >
        <div v-for="row in sortedQuotePositions" :key="`position-mobile-${row.id}-${row.description_id || 'custom'}`" class="mobile-position-card mb-1">
          <b-card class="ultra-compact-card">
            <div class="mobile-content-ultra">
              <!-- First row: Date, Description, Action Button -->
              <div class="d-flex align-items-center mb-1">
                <div class="mobile-drag-handle mr-2" style="cursor: move; color: #6c757d; flex-shrink: 0;">
                  <FontAwesomeIcon :icon="['fas', 'grip-vertical']" style="font-size: 0.7rem;" />
                </div>

                <div class="flex-grow-1 text-center mobile-center-block">
                  <div class="text-dark font-weight-bold mobile-date">{{ row.formatted_date }}</div>
                  <div class="text-dark mobile-description-inline">
                    <span v-if="row.description_id">
                      {{ quote.language == 'en' ? row.description_field.description_en : row.description_field.description_de }}
                    </span>
                    <span v-else>
                      {{ row.description }}
                    </span>
                  </div>
                  <div v-if="row.remark" class="text-muted mobile-remark">{{ row.remark }}</div>
                </div>

                <b-dropdown variant="outline-primary" dropleft no-caret size="sm" class="compact-dropdown ml-2">
                  <template #button-content>
                    <FontAwesomeIcon :icon="['fas', 'ellipsis-h']" style="font-size: 0.7rem;" />
                  </template>

                  <b-dropdown-item variant="primary" :disabled="$wait.is(`quotePosition${row.id}`)" @click="selectedQuotePosition = row.id">
                    <FontAwesomeIcon :icon="['fas', 'edit']" />
                    <small>&nbsp;{{ $t('actions.edit') }}</small>
                  </b-dropdown-item>

                  <b-dropdown-item variant="info" :disabled="$wait.is(`quotePosition${row.id}`)" @click="openDuplicateQuotePositionModal(row.id)">
                    <FontAwesomeIcon :icon="['fas', 'copy']" />
                    <small>&nbsp;{{ $t('actions.duplicate') }}</small>
                  </b-dropdown-item>

                  <b-dropdown-item variant="danger" :disabled="$wait.is(`quotePosition${row.id}`)" @click="deleteQuotePosition(row)">
                    <FontAwesomeIcon :icon="['fas', 'times-circle']" />
                    <small>&nbsp;{{ $t('actions.delete') }}</small>
                  </b-dropdown-item>
                </b-dropdown>
              </div>

              <!-- Second row: Amount, Unit Price, and Total -->
              <div class="d-flex justify-content-between align-items-center" style="font-size: 0.65rem;">
                <div class="text-left">
                  <small class="text-muted d-block" style="font-size: 0.6rem;">{{ $t('table_labels.amount') }}</small>
                  <span class="text-dark">{{ row.amount }}</span>
                </div>
                <div class="text-center">
                  <small class="text-muted d-block" style="font-size: 0.6rem;">{{ $t('table_labels.unit_price') }}</small>
                  <span class="text-dark">{{ row.formatted_price }}</span>
                </div>
                <div class="text-right">
                  <small class="text-muted d-block" style="font-size: 0.6rem;">{{ $t('table_labels.sum_price') }}</small>
                  <span class="text-dark font-weight-bold">{{ row.formatted_total }}</span>
                </div>
              </div>
            </div>
          </b-card>
        </div>
      </draggable>

      <!-- Mobile Summary - ultra compact -->
      <b-card class="mt-1 bg-light ultra-compact-summary">
        <div class="d-flex justify-content-between align-items-center" style="font-size: 0.75rem;">
          <span class="font-weight-bold text-dark">{{ $t('table_labels.subtotal') }}:</span>
          <span class="font-weight-bold text-dark">{{ formatPrice(subtotal) }}</span>
        </div>
        <div v-if="quote && quote.total !== null && quote.total !== undefined"
             class="d-flex justify-content-between align-items-center mt-1 pt-1 border-top" style="font-size: 0.75rem;">
          <span class="font-weight-bold text-dark">{{ $t('table_labels.total') }}<template v-if="quote && quote.add_tax"> {{ $t('table_labels.incl_tax') }}</template>:</span>
          <span class="font-weight-bold text-dark">
            {{ quote.total }}
          </span>
        </div>
      </b-card>
    </div>

    <QuotePositionModal
      v-model="selectedQuotePosition"
      :loaded-id="selectedQuotePosition"
      :quote="quote"
      @updated="update"
      @deleted="deleteFromModal(selectedQuotePosition)"
      @hidden="selectedQuotePosition = null"
    />

    <b-modal v-if="showDuplicateQuotePositionModal" v-model="showDuplicateQuotePositionModal" size="md" hide-footer :title="$t('messages.how_many_duplicates')" static>
      <b-form data-vv-scope="quote-position-duplicate-form">
        <b-form-group>
          <b-form-input
            v-model="num_of_duplicates"
            v-validate="'required|numeric|min:1|max:20'"
            type="number"
            step="1"
            min="1"
            max="20"
            data-vv-value-path="localValue"
            data-vv-name="num_of_duplicates"
            :state="errors.has('num_of_duplicates', 'quote-position-duplicate-form') ? false : null"
            aria-describedby="input-feedback-num_of_duplicates"
          ></b-form-input>
          <b-form-invalid-feedback id="input-feedback-num_of_duplicates">
            <template v-show="errors.has('num_of_duplicates', 'quote-position-duplicate-form')">{{ errors.first('num_of_duplicates', 'quote-position-duplicate-form') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>
        <div class="pull-right text-right">
          <b-button variant="outline-danger" class="mr-2" @click="closeDuplicateQuotePositionModal">{{ this.$t('actions.cancel_de') }}</b-button>
          <b-button :disabled="errors.any()" :variant="'outline-' + (!errors.any() ? 'dark' : 'danger')" @click="duplicate">
            {{ $t('actions.ok') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>
<style scoped>
/* Desktop drag handle styles */
.drag-handle {
  color: #6c757d;
  padding: 0.25rem;
  font-size: 0.9em;
  user-select: none;
  line-height: 1;
  display: inline-block;
}

.drag-handle:hover {
  color: #495057;
  background: #f8f9fa;
  border-radius: 3px;
}

/* Desktop sortable styles */
.sortable-ghost {
  opacity: 0.4;
  background: #f8f9fa !important;
}

.sortable-drag {
  opacity: 0.9;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

tbody tr {
  transition: all 0.3s ease;
}

/* Mobile styles - full card draggable */
.mobile-position-card {
  position: relative;
}

.ultra-compact-card {
  padding: 0.25rem !important;
  margin-bottom: 0.25rem;
  min-height: auto;
}

.ultra-compact-card .card-body {
  padding: 0.25rem !important;
}

.draggable-card {
  transition: all 0.2s ease;
}

.compact-dropdown {
  flex-shrink: 0;
}

.compact-dropdown .btn-sm {
  padding: 0.125rem 0.25rem;
  font-size: 0.7rem;
  line-height: 1;
  cursor: pointer;
}

.mobile-drag-handle {
  user-select: none;
  padding: 0.125rem 0.25rem;
  touch-action: none;
}

.mobile-content-ultra {
  min-height: 0;
  padding: 0.25rem;
}

.mobile-center-block {
  min-width: 0;
  overflow: hidden;
  padding: 0 0.25rem;
}

.mobile-date {
  font-size: 0.7rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-description-inline {
  font-size: 0.65rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-remark {
  font-size: 0.6rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-style: italic;
}

.ultra-compact-summary {
  padding: 0.375rem !important;
}

.ultra-compact-summary .card-body {
  padding: 0.375rem !important;
}

.sortable-ghost-mobile {
  opacity: 0.4;
}

.sortable-ghost-mobile .card {
  background: #f8f9fa !important;
  border: 2px dashed #dee2e6 !important;
  transform: rotate(0deg) !important;
}

.sortable-drag-mobile {
  opacity: 0.9;
  transform: rotate(2deg) scale(1.02);
  z-index: 1000;
}

.sortable-drag-mobile .card {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
  border-color: #007bff !important;
}

/* Bootstrap spacing utilities for older versions */
.me-2 {
  margin-right: 0.5rem !important;
}

.flex-grow-1 {
  flex-grow: 1 !important;
}

/* Responsive table adjustments */
@media (max-width: 991.98px) {
  .table-responsive {
    overflow-x: auto !important;
  }
}

/* Additional mobile optimizations */
@media (max-width: 575.98px) {
  .mobile-position-card {
    margin-bottom: 0.125rem !important;
  }

  .ultra-compact-card {
    padding: 0.2rem !important;
  }

  .mobile-description-inline {
    font-size: 0.65rem !important;
  }

  .compact-dropdown .btn-sm {
    padding: 0.1rem 0.2rem;
  }

  .me-2 {
    margin-right: 0.25rem !important;
  }
}

/* Ensure proper spacing for second row */
.mobile-content-ultra .d-flex:last-child > div {
  flex: 1;
}

.mobile-content-ultra .d-flex:last-child > div:first-child {
  padding-left: 0;
  text-align: left !important;
}

.mobile-content-ultra .d-flex:last-child > div:nth-child(2) {
  text-align: center !important;
}

.mobile-content-ultra .d-flex:last-child > div:last-child {
  padding-right: 0;
  text-align: right !important;
}
</style>
